import {
  deleteInternalAccount,
  editInternalAccount,
  addInternalAccount,
} from "@/app/lib/actions/internal-accounts";
import { ItemContent, ItemTitle } from "@/components/ui/item";
import {
  ItemsListCard,
  ItemsListCardSkeleton,
} from "@/components/backoffice/items-list-card";
import {
  useInternalAccountsQuery,
  invalidateInternalAccountsQuery,
} from "@/lib/queries";
import { useQueryClient } from "@tanstack/react-query";
import * as z from "zod";

import {
  addInternalAccountSchema,
  editInternalAccountSchema,
} from "@/lib/schemas/internal-accounts";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Field,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "@/components/ui/field";
import { toast } from "sonner";

type InternalAccount = {
  id: number;
  name: string;
  siret: string;
  isActive: boolean;
};

function InternalAccountItemsListCard() {
  const queryClient = useQueryClient();

  const internalAccountsQuery = useInternalAccountsQuery({
    sort: { name: "asc" },
  });
  const handleCreate = async (
    internalAccount: z.infer<typeof addInternalAccountSchema>,
  ) => {
    const response = await addInternalAccount(internalAccount);
    if (!response.success) {
      toast.error(response.message);
    } else {
      toast.success("Nouveau compte interne créé.");
      invalidateInternalAccountsQuery(queryClient);
    }
  };

  const handleUpdate = async (
    internalAccount: z.infer<typeof editInternalAccountSchema>,
  ) => {
    const response = await editInternalAccount(internalAccount);
    if (!response.success) {
      toast.error(response.message);
    } else {
      toast.success("Compte interne modifié.");
      invalidateInternalAccountsQuery(queryClient);
    }
  };

  const handleDelete = async (id: { id: number }) => {
    const response = await deleteInternalAccount(id);
    if (!response.success) {
      toast.error(response.message);
    } else {
      toast.success("Compte interne supprimé.");
      invalidateInternalAccountsQuery(queryClient);
    }
  };

  if (internalAccountsQuery.isPending) return <ItemsListCardSkeleton />;
  const internalAccounts = internalAccountsQuery.data?.internalAccounts ?? [];

  return (
    <ItemsListCard<InternalAccount>
      title="Comptes internes"
      items={internalAccounts}
      createSchema={addInternalAccountSchema}
      editSchema={editInternalAccountSchema}
      defaultCreateValues={{ name: "", siret: "", isActive: true }}
      onCreate={handleCreate}
      onUpdate={handleUpdate}
      onDelete={handleDelete}
      renderItem={(item) => (
        <>
          <ItemContent>
            <ItemTitle>{item.name}</ItemTitle>
          </ItemContent>
        </>
      )}
      renderFormFields={(form) => (
        <FieldGroup>
          <form.Field name="name">
            {(field) => {
              const isInvalid =
                field.state.meta.isTouched && !field.state.meta.isValid;
              return (
                <Field data-invalid={isInvalid}>
                  <FieldLabel htmlFor={field.name}>Libellé</FieldLabel>
                  <Input
                    id={field.name}
                    name={field.name}
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                    aria-invalid={isInvalid}
                    maxLength={200}
                  />
                  {isInvalid && <FieldError errors={field.state.meta.errors} />}
                </Field>
              );
            }}
          </form.Field>
          <form.Field name="siret">
            {(field) => {
              const isInvalid =
                field.state.meta.isTouched && !field.state.meta.isValid;
              return (
                <Field data-invalid={isInvalid}>
                  <FieldLabel htmlFor={field.name}>SIRET</FieldLabel>
                  <Input
                    id={field.name}
                    name={field.name}
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                    aria-invalid={isInvalid}
                    maxLength={14}
                  />

                  {isInvalid && <FieldError errors={field.state.meta.errors} />}
                </Field>
              );
            }}
          </form.Field>
          <form.Field name="isActive">
            {(field) => (
              <Field>
                <FieldLabel htmlFor={field.name}>Statut</FieldLabel>
                <Switch
                  id="switch"
                  checked={field.state.value}
                  onCheckedChange={field.handleChange}
                />
              </Field>
            )}
          </form.Field>
        </FieldGroup>
      )}
    />
  );
}

export { InternalAccountItemsListCard };
